// Modify the React imports to include useMemo
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { getRdv } from '../services/api';

/**
 * Palette de couleurs pour les graphiques – 8 teintes distinctes réutilisées en rotation
 */
const COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#8884d8',
  '#82ca9d',
  '#ff7300',
  '#a4de6c'
];

const DashboardPage = () => {
  /**
   * Traductions
   */
  const { t } = useTranslation();

  /**
   * State principal
   */
  const [rdvs, setRdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Onglet catégorie (Particulier = tout sauf Tertiaire, Tertiaire = uniquement Tertiaire)
   */
  const [categoryTab, setCategoryTab] = useState(0); // 0 = Particulier, 1 = Tertiaire

  /**
   * Filtres
   */
  const [periodFilter, setPeriodFilter] = useState('all'); // jour | semaine | mois | all
  const [filialeFilter, setFilialeFilter] = useState('all'); // nom de filiale | all
  const [versionFilter, setVersionFilter] = useState('all'); // V1 | V2 | V3 | all
  const [startDate, setStartDate] = useState(''); // YYYY‑MM‑DD
  const [endDate, setEndDate] = useState('');   // YYYY‑MM‑DD
  const [groupBy, setGroupBy] = useState('filiales'); // filiales | confirmateurs | origines

  /**
   * Récupération initiale des RDV
   */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getRdv();
        setRdvs(data);
      } catch (err) {
        setError(t('dashboard.fetchError'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [t]);

  /** ------------------------------------------------------------------
   *  LISTES DÉRIVÉES
   * ------------------------------------------------------------------*/
  const filiales = useMemo(() => {
    const setFiliales = new Set();
    rdvs.forEach(r => setFiliales.add(r.filiale || t('status.Non défini')));
    return Array.from(setFiliales).sort((a, b) => a.localeCompare(b));
  }, [rdvs, t]);

  /** ------------------------------------------------------------------
   *  FILTRAGE DES DONNÉES (RDV)
   * ------------------------------------------------------------------*/
  const filteredRdvs = useMemo(() => {
    let tmp = rdvs;

    // 0 : filtrage par catégorie (onglet)
    if (categoryTab === 0) {
      // Particulier : tout sauf Tertiaire
      tmp = tmp.filter(r => (r.categorie || '').toLowerCase() !== 'tertiaire');
    } else {
      // Tertiaire : uniquement Tertiaire
      tmp = tmp.filter(r => (r.categorie || '').toLowerCase() === 'tertiaire');
    }

    // 1 : filtrage par intervalle précis
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end   = new Date(endDate);
      tmp = tmp.filter(r => {
        const d = new Date(r.rdv_date);
        return d >= start && d <= end;
      });
    // 2 : filtrage par période relative
    } else if (periodFilter !== 'all') {
      const now = new Date();
      let pStart, pEnd;
      switch (periodFilter) {
        case 'jour':
          pStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          pEnd   = new Date(pStart); pEnd.setDate(pEnd.getDate() + 1);
          break;
        case 'semaine': {
          const diff = (now.getDay() === 0 ? -6 : 1 - now.getDay()); // lundi = 0
          pStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff);
          pEnd   = new Date(pStart); pEnd.setDate(pEnd.getDate() + 7);
          break; }
        case 'mois':
          pStart = new Date(now.getFullYear(), now.getMonth(), 1);
          pEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 1);
          break;
        default:
          pStart = pEnd = null;
      }
      tmp = tmp.filter(r => {
        const d = new Date(r.rdv_date);
        return d >= pStart && d < pEnd;
      });
    }

    // 3 : filtrage filiale
    if (filialeFilter !== 'all') {
      tmp = tmp.filter(r => (r.filiale || t('status.Non défini')) === filialeFilter);
    }

    // 4 : filtrage version (V1, V2, V3) basé sur typeDossier
    if (versionFilter !== 'all') {
      tmp = tmp.filter(r => {
        const typeDossier = (r.typeDossier || '').toUpperCase();
        switch (versionFilter) {
          case 'V1':
            // ISO ESPAGNE mais PAS ISO ESPAGNE V (V2 ou V3)
            return typeDossier.includes('ISO ESPAGNE') && !typeDossier.includes('ISO ESPAGNE V');
          case 'V2':
            return typeDossier.includes('ISO ESPAGNE V2');
          case 'V3':
            return typeDossier.includes('ISO ESPAGNE V3');
          default:
            return true;
        }
      });
    }

    return tmp;
  }, [rdvs, categoryTab, periodFilter, filialeFilter, versionFilter, startDate, endDate, t]);

  /** ------------------------------------------------------------------
   *  STATISTIQUES & DONNÉES POUR GRAPHIQUES
   * ------------------------------------------------------------------*/
  const {
    stats,
    statusStats,
    statusList,
    filialeStatusData,
    equipeStatusData,
    cpStatusData,
    typeInstallData,
    confirmateurStatusData
  } = useMemo(() => {
    // Si aucune donnée filtrée → retourner des valeurs vides
    if (filteredRdvs.length === 0) {
      return {
        stats: null,
        statusStats: {},
        statusList: [],
        filialeStatusData: [],
        equipeStatusData: [],
        cpStatusData: [],
        typeInstallData: [],
        confirmateurStatusData: []
      };
    }

    /** ---------------- KPI généraux ----------------*/
    const totalRdvs = filteredRdvs.length;

    // Agrégations diverses
    const filialeCounts      = {};
    const equipeCounts       = {};
    const typeInstallCounts  = {};
    const cpCounts           = {};
    const surfacesEstimees   = [];
    const surfacesInstallees = [];

    filteredRdvs.forEach(rdv => {
      const filiale = rdv.filiale || t('status.Non défini');
      filialeCounts[filiale] = (filialeCounts[filiale] || 0) + 1;

      const eq = rdv.equipe || t('distanceManagement.noTeam');
      equipeCounts[eq] = (equipeCounts[eq] || 0) + 1;

      const ti = rdv.type_install || t('status.Non défini');
      typeInstallCounts[ti] = (typeInstallCounts[ti] || 0) + 1;

      const cp = rdv.cp || t('status.Non défini');
      cpCounts[cp] = (cpCounts[cp] || 0) + 1;

      if (rdv.surface_estime)   surfacesEstimees.push(+rdv.surface_estime);
      if (rdv.surface_installe) surfacesInstallees.push(+rdv.surface_installe);
    });

    const avgSurfaceEstimee   = surfacesEstimees.length ? (surfacesEstimees.reduce((a,b)=>a+b,0)/surfacesEstimees.length).toFixed(2) : 'N/A';
    const avgSurfaceInstallee = surfacesInstallees.length ? (surfacesInstallees.reduce((a,b)=>a+b,0)/surfacesInstallees.length).toFixed(2) : 'N/A';
    const totalSurfaceInstallee = surfacesInstallees.reduce((a,b)=>a+b,0);

    const topCPs = Object.entries(cpCounts)
                         .sort((a,b)=>b[1]-a[1])
                         .slice(0,5)
                         .map(([cp,count])=>({cp,count}));

    const stats = {
      totalRdvs,
      filialeCounts,
      equipeCounts,
      typeInstallCounts,
      avgSurfaceEstimee,
      avgSurfaceInstallee,
      totalSurfaceInstallee,
      topCPs
    };

    /** ---------------- Stats détaillées par status ----------------*/
    const statusStats = {};
    filteredRdvs.forEach(r => {
      const status = r.status || t('status.Non défini');
      if (!statusStats[status]) {
        statusStats[status] = { count: 0, surfacesEstimee: [], surfacesInstallee: [] };
      }
      statusStats[status].count += 1;
      if (r.surface_estime)   statusStats[status].surfacesEstimee.push(+r.surface_estime);
      if (r.surface_installe) statusStats[status].surfacesInstallee.push(+r.surface_installe);
    });
    Object.keys(statusStats).forEach(s => {
      const obj = statusStats[s];
      obj.avgSurfaceEstimee   = obj.surfacesEstimee.length   ? (obj.surfacesEstimee.reduce((a,b)=>a+b,0)/obj.surfacesEstimee.length).toFixed(2) : 'N/A';
      obj.avgSurfaceInstallee = obj.surfacesInstallee.length ? (obj.surfacesInstallee.reduce((a,b)=>a+b,0)/obj.surfacesInstallee.length).toFixed(2) : 'N/A';
      obj.totalSurfaceInstallee = obj.surfacesInstallee.reduce((a,b)=>a+b,0);
    });

    const statusList = Object.keys(statusStats).sort();

    /** ---------------- Données pour BarCharts ----------------*/
    const filialeStatusData = {};
    const equipeStatusData  = {};
    const cpStatusDataTmp   = {};

    filteredRdvs.forEach(r => {
      const filiale = r.filiale || t('status.Non défini');
      const equipe  = r.equipe  || t('distanceManagement.noTeam');
      const cp      = r.cp      || t('status.Non défini');
      const status  = r.status  || t('status.Non défini');

      // filiale
      if (!filialeStatusData[filiale]) {
        filialeStatusData[filiale] = { filiale };
        statusList.forEach(s => (filialeStatusData[filiale][s] = 0));
      }
      filialeStatusData[filiale][status] += 1;

      // equipe
      if (!equipeStatusData[equipe]) {
        equipeStatusData[equipe] = { equipe };
        statusList.forEach(s => (equipeStatusData[equipe][s] = 0));
      }
      equipeStatusData[equipe][status] += 1;

      // cp (sera filtré top 5 ensuite)
      if (!cpStatusDataTmp[cp]) {
        cpStatusDataTmp[cp] = { cp, total: 0 };
        statusList.forEach(s => (cpStatusDataTmp[cp][s] = 0));
      }
      cpStatusDataTmp[cp][status] += 1;
      cpStatusDataTmp[cp].total  += 1;
    });

    const cpStatusData = Object.values(cpStatusDataTmp)
                               .sort((a,b)=>b.total - a.total)
                               .slice(0,5);

    /** ---------------- Données pour PieChart ----------------*/
    const typeInstallData = Object.entries(typeInstallCounts).map(([name,value])=>({name,value}));

    /** ---------------- NOUVEAU: Données pour graphique confirmateur_interne ----------------*/
    // Créer un objet pour stocker les données par status et confirmateur_interne
    const confirmateurStatusDataTmp = {
      true: {},
      false: {}
    };

    // Initialiser les compteurs pour chaque status
    statusList.forEach(status => {
      confirmateurStatusDataTmp.true[status] = 0;
      confirmateurStatusDataTmp.false[status] = 0;
    });

    // Comptabiliser les données
    filteredRdvs.forEach(rdv => {
      const status = rdv.status || t('status.Non défini');
      const isConfirmateurInterne = rdv.confirmateur_interne ? "true" : "false";
      
      confirmateurStatusDataTmp[isConfirmateurInterne][status] = 
        (confirmateurStatusDataTmp[isConfirmateurInterne][status] || 0) + 1;
    });

    // Formater les données pour le graphique
    const confirmateurStatusData = [
      {
        name: t('dashboard.confirmateurInterne.true'),
        ...confirmateurStatusDataTmp.true
      },
      {
        name: t('dashboard.confirmateurInterne.false'),
        ...confirmateurStatusDataTmp.false
      }
    ];

    return {
      stats,
      statusStats,
      statusList,
      filialeStatusData: Object.values(filialeStatusData),
      equipeStatusData: Object.values(equipeStatusData),
      cpStatusData,
      typeInstallData,
      confirmateurStatusData
    };
  }, [filteredRdvs, t]);

  /** ------------------------------------------------------------------
   *  RENDU
   * ------------------------------------------------------------------*/
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      {/* ---------------------------------------------------------------- KPI & Filtres ---------------------------------------------------------------- */}
      <Typography variant="h4" gutterBottom>{t('dashboard.title')}</Typography>

      {/* Onglets Catégorie */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={categoryTab}
          onChange={(e, newValue) => setCategoryTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              fontWeight: 'bold',
              fontSize: '1rem'
            }
          }}
        >
          <Tab label={t('dashboard.tabs.particulier')} />
          <Tab label={t('dashboard.tabs.tertiaire')} />
        </Tabs>
      </Box>

      {/* Filtres */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        {/* Période */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>{t('dashboard.periodLabel')}</InputLabel>
          <Select value={periodFilter} label={t('dashboard.periodLabel')} onChange={e=>setPeriodFilter(e.target.value)}>
            <MenuItem value="all">{t('filters.all')}</MenuItem>
            <MenuItem value="jour">{t('dashboard.period.day')}</MenuItem>
            <MenuItem value="semaine">{t('dashboard.period.week')}</MenuItem>
            <MenuItem value="mois">{t('dashboard.period.month')}</MenuItem>
          </Select>
        </FormControl>

        {/* Filiale */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>{t('filters.branch')}</InputLabel>
          <Select value={filialeFilter} label={t('filters.branch')} onChange={e=>setFilialeFilter(e.target.value)}>
            <MenuItem value="all">{t('filters.all')}</MenuItem>
            {filiales.map(f=>(<MenuItem key={f} value={f}>{f}</MenuItem>))}
          </Select>
        </FormControl>

        {/* Version (V1, V2, V3) */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>{t('dashboard.versionLabel')}</InputLabel>
          <Select value={versionFilter} label={t('dashboard.versionLabel')} onChange={e=>setVersionFilter(e.target.value)}>
            <MenuItem value="all">{t('filters.all')}</MenuItem>
            <MenuItem value="V1">V1</MenuItem>
            <MenuItem value="V2">V2</MenuItem>
            <MenuItem value="V3">V3</MenuItem>
          </Select>
        </FormControl>

        {/* Intervalle dates */}
        <TextField label={t('dashboard.startDate')} type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} InputLabelProps={{shrink:true}} size="small" />
        <TextField label={t('dashboard.endDate')} type="date" value={endDate}   onChange={e=>setEndDate(e.target.value)}   InputLabelProps={{shrink:true}} size="small" />

        {/* Reset */}
        <Box sx={{ alignSelf:'center' }}>
          <Typography variant="button" sx={{ cursor:'pointer', color:'primary.main' }} onClick={()=>{setPeriodFilter('all'); setFilialeFilter('all'); setVersionFilter('all'); setStartDate(''); setEndDate('');}}>
            {t('dashboard.resetFilters')}
          </Typography>
        </Box>
      </Box>

      {/* ---------------------------------------------------------------- KPI principales ---------------------------------------------------------------- */}
      {stats ? (
        <>
          <Box sx={{ display:'flex', flexWrap:'wrap', gap:4, mb:4 }}>
            {/* Total RDV */}
            <Paper elevation={3} sx={{ p:3, textAlign:'center', flex:'1 1 200px' }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>{t('dashboard.kpi.totalRdvs')}</Typography>
              <Typography variant="h4">{stats.totalRdvs}</Typography>
              {Object.entries(statusStats).map(([s,v])=> (
                <Typography variant="body2" key={`tot-${s}`}>{s}: {v.count}</Typography>
              ))}
            </Paper>
            {/* Moy. estimée */}
            <Paper elevation={3} sx={{ p:3, textAlign:'center', flex:'1 1 200px' }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>{t('dashboard.kpi.avgEst')}</Typography>
              <Typography variant="h4">{stats.avgSurfaceEstimee} m²</Typography>
              {Object.entries(statusStats).map(([s,v])=> (
                <Typography variant="body2" key={`avgEst-${s}`}>{s}: {v.avgSurfaceEstimee} m²</Typography>
              ))}
            </Paper>
            {/* Moy. installée */}
            <Paper elevation={3} sx={{ p:3, textAlign:'center', flex:'1 1 200px' }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>{t('dashboard.kpi.avgIns')}</Typography>
              <Typography variant="h4">{stats.avgSurfaceInstallee !== 'N/A' ? `${stats.avgSurfaceInstallee} m²` : 'N/A'}</Typography>
              {Object.entries(statusStats).map(([s,v])=> (
                <Typography variant="body2" key={`avgIns-${s}`}>{s}: {v.avgSurfaceInstallee} m²</Typography>
              ))}
            </Paper>
            {/* Total installés */}
            <Paper elevation={3} sx={{ p:3, textAlign:'center', flex:'1 1 200px' }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>{t('dashboard.kpi.totIns')}</Typography>
              <Typography variant="h4">{stats.totalSurfaceInstallee}</Typography>
              {Object.entries(statusStats).map(([s,v])=> (
                <Typography variant="body2" key={`totIns-${s}`}>{s}: {v.totalSurfaceInstallee} m²</Typography>
              ))}
            </Paper>
          </Box>

          {/* ---------------------------------------------------------------- GRAPHIQUES ---------------------------------------------------------------- */}
          {/* 1. Par filiale */}
          <Box sx={{ mb:5 }}>
            <Paper elevation={3} sx={{ p:3 }}>
              <Typography variant="h6" gutterBottom>{t('dashboard.byBranch')}</Typography>
              <ResponsiveContainer width="100%" height={450}>
                <BarChart data={filialeStatusData} margin={{top:20,right:20,left:0,bottom:20}}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="filiale" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {statusList.map((status,idx)=>(
                    <Bar key={status} dataKey={status} name={status} fill={COLORS[idx % COLORS.length]} barSize={50} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* 2. Par équipe */}
          <Box sx={{ mb:5 }}>
            <Paper elevation={3} sx={{ p:3 }}>
              <Typography variant="h6" gutterBottom>{t('dashboard.byTeam')}</Typography>
              <ResponsiveContainer width="100%" height={450}>
                <BarChart data={equipeStatusData} margin={{top:20,right:20,left:0,bottom:20}}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="equipe" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {statusList.map((status,idx)=>(
                    <Bar key={status} dataKey={status} name={status} fill={COLORS[idx % COLORS.length]} barSize={50} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* 3. Top 5 CP */}
          <Box sx={{ mb:5 }}>
            <Paper elevation={3} sx={{ p:3 }}>
              <Typography variant="h6" gutterBottom>{t('dashboard.topCp')}</Typography>
              <ResponsiveContainer width="100%" height={450}>
                <BarChart data={cpStatusData} margin={{top:20,right:20,left:0,bottom:20}}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="cp" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {statusList.map((status,idx)=>(
                    <Bar key={status} dataKey={status} name={status} fill={COLORS[idx % COLORS.length]} barSize={50} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* NOUVEAU: 4. Par confirmateur_interne */}
          <Box sx={{ mb:5 }}>
            <Paper elevation={3} sx={{ p:3 }}>
              <Typography variant="h6" gutterBottom>{t('dashboard.byConfirmateurInterne')}</Typography>
              <ResponsiveContainer width="100%" height={450}>
                <BarChart data={confirmateurStatusData} margin={{top:20,right:20,left:0,bottom:20}}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {statusList.map((status,idx)=>(
                    <Bar key={status} dataKey={status} name={status} fill={COLORS[idx % COLORS.length]} barSize={50} stackId="a" />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* 5. Types d'installation */}
          <Box sx={{ mb:5 }}>
            <Paper elevation={3} sx={{ p:3 }}>
              <Typography variant="h6" gutterBottom>{t('dashboard.typeInstall')}</Typography>
              <ResponsiveContainer width="100%" height={450}>
                <PieChart>
                  <Pie data={typeInstallData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={150}
                       label={({name,percent})=>`${name}: ${(percent*100).toFixed(0)}%`}>
                    {typeInstallData.map((_,idx)=>(<Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />))}
                  </Pie>
                  <Tooltip formatter={v=>[v,t('dashboard.quantity')]} />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Box>
        </>
      ) : (
        <Typography variant="body1">{t('dashboard.noData')}</Typography>
      )}
    </Container>
  );
};

export default DashboardPage;